import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMedia } from 'react-use';
import {
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  IconButton,
  useSnackbar,
} from '@c2fo/liquidity';
import { EllipsisVerticalIcon } from '@c2fo/liquidity/icons';
import { useBuyerNavigationLayoutContext } from '@/enterprise/components/BuyerNavigationLayout';
import useEnableAutomations from '@/enterprise/components/useEnableAutomations';
import { downloadPlayableInvoicesCsv } from '@/enterprise/data/downloadPlayableInvoicesCsv';
import { PlayableInvoiceSearchFilters } from '@/enterprise/data/usePlayableInvoices';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import RecurringRulesForm from '@/enterprise/features/recurringRules/RecurringRulesForm';
import { RecurringRulesMenuItem } from '@/enterprise/features/takerMarketTabs/components/TabActionsDropdown';
import useFeature from '@/lib/features';
import { useReporting } from '@/reporting';
import IntercomDataTarget from '@/reporting/IntercomDataTarget';
import { buildDivisionInvoiceCsvFilename } from '@/utils/buildInvoiceCsvFilename';
import useInvoiceDownloadUrl from '@/utils/useInvoiceDownloadUrl';
import useRestrictions from '@/utils/useRestrictions';

type DownloadScope = 'all' | 'included' | 'excluded';

const EligibleInvoiceActionsDropdown = ({
  invoiceDownloadUrl,
  invoiceDownloadUrlDisabled,
  takerMarket,
}: {
  invoiceDownloadUrl?: string;
  invoiceDownloadUrlDisabled?: boolean;
  takerMarket: TakerMarket;
}) => {
  const { t } = useTranslation();
  const showSnackbar = useSnackbar();
  const { trackEnterpriseEvent } = useReporting();
  const [showRecurringRulesModal, setShowRecurringRulesModal] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const { getRestrictions } = useRestrictions();
  const enableAutomations = useEnableAutomations();
  const [invoiceApiMigrationEnabled] = useFeature('enterprise-ui_invoiceApiMigration');
  const { canEditRecurringRules } = getRestrictions([takerMarket]);
  const isMobile = useMedia('(max-width: 768px)');
  const {
    urlParams: { marketId, takerId },
  } = useBuyerNavigationLayoutContext();
  const invoiceDownloadUrlIncluded = useInvoiceDownloadUrl({
    filters: { included: [true] },
    marketId,
    takerId,
    type: 'eligible',
  });
  const invoiceDownloadUrlExcluded = useInvoiceDownloadUrl({
    filters: { included: [false] },
    marketId,
    takerId,
    type: 'eligible',
  });

  const openEligibleInvoiceActionsDropdown = () => {
    trackEnterpriseEvent('eligible-invoice-actions::clicked', {
      takerMarket: { takerId: takerMarket.takerDivisionId, marketUuid: takerMarket.marketUuid },
      type: 'division',
    });
  };

  const openRecurringRulesModal = () => {
    setShowRecurringRulesModal(true);
    trackEnterpriseEvent('recurring-rules::opened', {
      referrer: 'division-actions-dropdown',
    });
  };

  const closeRecurringRulesModal = () => {
    setShowRecurringRulesModal(false);
    trackEnterpriseEvent('recurring-rules::closed');
  };

  const searchForScope = (scope: DownloadScope): PlayableInvoiceSearchFilters | undefined => {
    if (scope === 'included') {
      return { supplierExcluded: false };
    }

    if (scope === 'excluded') {
      return { supplierExcluded: true };
    }

    return undefined;
  };

  const handleSeaDownload = async (scope: DownloadScope) => {
    if (invoiceDownloadUrlDisabled || isDownloading) {
      return;
    }

    setIsDownloading(true);
    try {
      await downloadPlayableInvoicesCsv({
        makerOrganizationUuid: takerMarket.makerOrganizationUuid,
        takerMarkets: [takerMarket],
        search: searchForScope(scope),
        options: {
          sortBy: 'amount',
          sortDirection: 'DESC',
        },
        filename: buildDivisionInvoiceCsvFilename({
          makerName: takerMarket.makerOrganizationName,
          divisionId: takerMarket.takerDivisionId,
          invoiceType: 'eligible',
        }),
      });
    } catch {
      showSnackbar({ message: t('core.downloadError') });
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadDisabled = Boolean(invoiceDownloadUrlDisabled) || isDownloading;

  const downloadMenuItems = [
    {
      id: 'all' as const,
      url: invoiceDownloadUrl,
      labelDesktop: t('core.allInvoices'),
      labelMobile: t('core.downloadAllInvoices'),
    },
    {
      id: 'included' as const,
      url: invoiceDownloadUrlIncluded,
      labelDesktop: t('core.allIncludedInvoices'),
      labelMobile: t('core.downloadAllIncludedInvoices'),
    },
    {
      id: 'excluded' as const,
      url: invoiceDownloadUrlExcluded,
      labelDesktop: t('core.allExcludedInvoices'),
      labelMobile: t('core.downloadAllExcludedInvoices'),
    },
  ];

  const renderDownloadMenuItems = () =>
    downloadMenuItems.map(({ id, url, labelDesktop, labelMobile }) => {
      const label = isMobile ? labelMobile : labelDesktop;

      if (invoiceApiMigrationEnabled) {
        return (
          <DropdownMenuItem
            key={id}
            disabled={downloadDisabled}
            onSelect={() => {
              void handleSeaDownload(id);
            }}
          >
            {label}
          </DropdownMenuItem>
        );
      }

      return (
        <DropdownMenuItem key={id} asChild disabled={downloadDisabled}>
          <a href={url} target="_self">
            {label}
          </a>
        </DropdownMenuItem>
      );
    });

  const dropdownConfig = {
    dropdownMenuTrigger: (
      <IconButton icon={EllipsisVerticalIcon} name={t('taker.invoices.eligible.actionDropdown')} variant="primary" />
    ),
    dropdownMenuContent: (
      <>
        {enableAutomations ? (
          <RecurringRulesMenuItem onClick={openRecurringRulesModal} readOnly={!canEditRecurringRules} />
        ) : null}
        {isMobile ? (
          renderDownloadMenuItems()
        ) : (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              disabled={downloadDisabled}
              className={cn({ 'cursor-not-allowed opacity-38': downloadDisabled })}
            >
              {t('core.downloadInvoices')}
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent className="w-52">{renderDownloadMenuItems()}</DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        )}
      </>
    ),
  };

  return (
    <IntercomDataTarget target="eligibleInvoiceActionsDropdown">
      <RecurringRulesForm
        defaultValues={{ takerMarkets: [takerMarket] }}
        isForSingleDivision
        onClose={closeRecurringRulesModal}
        open={showRecurringRulesModal}
        referrer="divisionDetails"
        takerMarkets={[takerMarket]}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={openEligibleInvoiceActionsDropdown}>
          {dropdownConfig.dropdownMenuTrigger}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          avoidCollisions={false}
          collisionPadding={24}
          data-intercom-target="eligibleInvoiceActionsDropdownMenu"
        >
          {dropdownConfig.dropdownMenuContent}
        </DropdownMenuContent>
      </DropdownMenu>
    </IntercomDataTarget>
  );
};

export default EligibleInvoiceActionsDropdown;
